import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { buildDeck } from '../constants/decks.js';
import DeckStop from './DeckStop.jsx';
import DeckQuizControls from './DeckQuizControls.jsx';
import ProjectorControls, { ConnectionDot } from './ProjectorControls.jsx';
import DeckOverlays from './DeckOverlays.jsx';
import QrStage from './QrStage.jsx';
import DeckBackground from './DeckBackground.jsx';
import { useDeckPosition } from './useDeckPosition.js';
import { useDeckKeys } from './useDeckKeys.js';
import { stopLabel } from './stopLabel.js';
import { preloadHandLandmarker } from './camtags/useHandLandmarker.js';
import './deck.css';

/**
 * The projected screen — what the class looks at. It holds no position of
 * its own: `useDeckPosition` renders whatever the server says, and an arrow
 * press here publishes the result the same way a press on the control panel
 * does, so a presenter remote plugged into this machine still drives the
 * lesson.
 *
 * Two overlays are shared with the control panel because they are moments on
 * the projector — the QR code and the fullscreen understanding check — and
 * live in the server's position. The slide list is not: it is how the
 * instructor decides where to go next, and stays on the screen it was opened
 * from.
 */
export default function Deck({ room, onExit }) {
  const deck = useMemo(
    () => buildDeck(room.roomCode, room.questions),
    [room.roomCode, room.questions]
  );
  const stops = deck.stops;

  // A deck with the camera slide starts loading the hand model while the
  // cover is up, so a slow classroom network does not push the slide into
  // its demo. Only the projector does this; the panel's miniatures never
  // open the camera.
  const hasCamera = stops.some((s) => s.kind === 'cam-tags');
  useEffect(() => {
    if (hasCamera) preloadHandLandmarker();
  }, [hasCamera]);

  const { index, substep, direction, lastCount, stop, stepsOf, goRel, goTo } =
    useDeckPosition(room, stops, { isProjector: true });

  const [showOverview, setShowOverview] = useState(false);

  const overlay = room.position?.overlay ?? null;
  const setOverlay = useCallback(
    (next) => room.actions.setOverlay(next).catch(() => {}),
    [room]
  );
  const toggleOverlay = useCallback(
    (kind) => setOverlay(overlay === kind ? null : kind),
    [overlay, setOverlay]
  );

  useDeckKeys(
    {
      ArrowRight: () => goRel(1),
      PageDown: () => goRel(1),
      ' ': () => goRel(1),
      ArrowLeft: () => goRel(-1),
      PageUp: () => goRel(-1),
      f: () => {
        if (document.fullscreenElement) document.exitFullscreen?.();
        else document.documentElement.requestFullscreen?.().catch(() => {});
      },
      o: () => setShowOverview((v) => !v),
      c: () => toggleOverlay('check'),
      q: () => toggleOverlay('qr'),
      Escape: () => {
        setShowOverview(false);
        setOverlay(null);
      },
    },
    (n) => goTo(n - 1)
  );

  if (!stop) return null;

  const findQuestion = (order) =>
    room.questions.find((q) => q.order_index === order) || null;

  return (
    <div className="deck-root" data-theme={deck.meta?.theme}>
      <DeckBackground kind={stop.kind} theme={deck.meta?.theme} />
      <div className="deck-stage" data-kind={stop.kind} data-part={stop.part || ''} data-direction={direction} key={index}>
        <DeckStop
          stop={stop}
          substep={substep}
          deck={deck}
          room={room}
          findQuestion={findQuestion}
          onOpenQr={() => setOverlay('qr')}
        />
      </div>

      {/* edge click zones */}
      <button className="deck-edge deck-edge-left" aria-label="ย้อนกลับ" onClick={() => goRel(-1)} />
      <button className="deck-edge deck-edge-right" aria-label="ถัดไป" onClick={() => goRel(1)} />

      {!['cover', 'divider', 'results', 'outro'].includes(stop.kind) && (
        <div className="deck-footer">
          {deck.meta?.unit && <span>{deck.meta.unit}</span>}
          {stepsOf(index) > 0 && (
            <span className="deck-substeps" aria-hidden="true">
              {Array.from({ length: stepsOf(index) + 1 }).map((_, i) => (
                <span key={i} className={`deck-substep-dot ${i <= substep ? 'is-done' : ''}`} />
              ))}
            </span>
          )}
          <span className="num">{index + 1} / {lastCount}</span>
        </div>
      )}

      <DeckQuizControls room={room} stop={stop} />
      <DeckOverlays
        room={room}
        showCheck={overlay === 'check'}
        onCloseCheck={() => setOverlay(null)}
      />
      <ProjectorControls onExit={onExit} />
      <ConnectionDot connected={room.connected} />

      {overlay === 'qr' && <QrStage roomCode={room.roomCode} onClose={() => setOverlay(null)} />}

      {showOverview && (
        <div className="deck-overview" onClick={() => setShowOverview(false)}>
          <div className="deck-overview-panel" onClick={(e) => e.stopPropagation()}>
            <h2>สไลด์ทั้งหมด · {lastCount} จอ</h2>
            <ol>
              {stops.map((s, i) => (
                <li key={i}>
                  <button
                    className={i === index ? 'is-current' : ''}
                    onClick={() => {
                      goTo(i);
                      setShowOverview(false);
                    }}
                  >
                    <span className="deck-overview-num">{i + 1}</span>
                    <span className="deck-overview-kind">{stopLabel(s, findQuestion)}</span>
                  </button>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
